/**
 * Pattern Generator Module
 * 재미있는 번호 선택 경험을 위한 다양한 패턴 생성기
 * 
 * 중요: 모든 패턴의 당첨 확률은 동일합니다 (1/8,145,060)
 * 이 모듈은 순전히 선택의 재미를 위한 것입니다.
 */

// ============= 패턴 타입 정의 =============
export const PATTERN_TYPES = {
  MATHEMATICAL: {
    name: '수학적 패턴',
    description: '수학적 규칙을 따르는 재미있는 패턴',
    icon: '🔢',
    patterns: ['fibonacci', 'prime', 'squares', 'triangular']
  },
  VISUAL: {
    name: '시각적 패턴',
    description: '로또 용지에 그려지는 모양',
    icon: '🎨',
    patterns: ['heart', 'star', 'cross', 'diagonal', 'smile']
  },
  STATISTICAL: {
    name: '통계적 재미',
    description: '통계를 활용한 재미있는 선택 (확률은 동일)',
    icon: '📊',
    patterns: ['hot', 'cold', 'balanced', 'avoided']
  },
  RANDOM_FUN: {
    name: '랜덤 재미',
    description: '독특한 랜덤 규칙',
    icon: '🎲',
    patterns: ['chaos', 'quantum', 'cosmic', 'nature']
  }
};

// ============= 수학적 패턴 생성기 =============

/**
 * 피보나치 수열 패턴
 */
export function generateFibonacciPattern() {
  const fibonacci = [1, 1, 2, 3, 5, 8, 13, 21, 34];
  const lottoNumbers = fibonacci.filter(n => n <= 45).slice(0, 6);
  
  // 6개가 안 되면 피보나치 변형 추가
  while (lottoNumbers.length < 6) {
    const lastTwo = lottoNumbers.slice(-2);
    const next = (lastTwo[0] + lastTwo[1]) % 45 || 1;
    if (!lottoNumbers.includes(next)) {
      lottoNumbers.push(next);
    } else {
      lottoNumbers.push(Math.floor(Math.random() * 45) + 1);
    }
  }
  
  return {
    numbers: lottoNumbers.slice(0, 6).sort((a, b) => a - b),
    patternName: '피보나치 수열',
    description: '자연의 황금비를 따르는 수열',
    funFactor: '⭐⭐⭐⭐',
    mathematicalBeauty: '🌻'
  };
}

/**
 * 소수 패턴
 */
export function generatePrimePattern() {
  const primes = [2, 3, 5, 7, 11, 13, 17, 19, 23, 29, 31, 37, 41, 43];
  
  // 랜덤하게 6개 선택
  const selected = [];
  const availablePrimes = [...primes];
  
  for (let i = 0; i < 6 && availablePrimes.length > 0; i++) {
    const index = Math.floor(Math.random() * availablePrimes.length);
    selected.push(availablePrimes.splice(index, 1)[0]);
  }
  
  return {
    numbers: selected.sort((a, b) => a - b),
    patternName: '소수의 신비',
    description: '나누어지지 않는 특별한 숫자들',
    funFactor: '⭐⭐⭐⭐⭐',
    mathematicalBeauty: '🔐'
  };
}

/**
 * 제곱수 패턴
 */
export function generateSquaresPattern() {
  const squares = [1, 4, 9, 16, 25, 36];
  const mixed = [...squares];
  
  // 일부를 변형
  for (let i = 0; i < 3; i++) {
    const index = Math.floor(Math.random() * mixed.length);
    mixed[index] = Math.min(45, mixed[index] + Math.floor(Math.random() * 5));
  }
  
  return {
    numbers: mixed.sort((a, b) => a - b),
    patternName: '제곱수 변주',
    description: '완전제곱수와 그 주변',
    funFactor: '⭐⭐⭐',
    mathematicalBeauty: '📐'
  };
}

/**
 * 삼각수 패턴
 */
export function generateTriangularPattern() {
  const triangular = [1, 3, 6, 10, 15, 21, 28, 36, 45];
  const selected = [];
  
  // 삼각수 중 6개 랜덤 선택
  const available = [...triangular];
  for (let i = 0; i < 6 && available.length > 0; i++) {
    const index = Math.floor(Math.random() * available.length);
    selected.push(available.splice(index, 1)[0]);
  }
  
  return {
    numbers: selected.sort((a, b) => a - b),
    patternName: '삼각수 패턴',
    description: '피라미드를 쌓는 숫자들',
    funFactor: '⭐⭐⭐⭐',
    mathematicalBeauty: '🔺'
  };
}

// ============= 시각적 패턴 생성기 =============

/**
 * 하트 모양 패턴
 * 로또 용지에서 하트 모양을 그리는 번호들
 */
export function generateHeartPattern() {
  // 7x7 그리드에서 하트 모양 좌표
  const heartCoords = [
    8, 9, 11, 12,      // 상단
    14, 15, 16, 17, 18, 19, 20,  // 중간 넓은 부분
    22, 23, 24, 25, 26, 27,      // 중하단
    30, 31, 32, 33,    // 하단
    37, 38             // 끝
  ];
  
  // 하트 좌표에서 6개 선택
  const selected = [];
  for (let i = 0; i < 6; i++) {
    const index = Math.floor(Math.random() * heartCoords.length);
    const num = heartCoords.splice(index, 1)[0];
    if (num <= 45) selected.push(num);
  }
  
  // 부족하면 채우기
  while (selected.length < 6) {
    const num = Math.floor(Math.random() * 45) + 1;
    if (!selected.includes(num)) selected.push(num);
  }
  
  return {
    numbers: selected.slice(0, 6).sort((a, b) => a - b),
    patternName: '하트 그리기',
    description: '사랑을 담은 번호 선택 ❤️',
    funFactor: '⭐⭐⭐⭐⭐',
    visualAppeal: '💕'
  };
}

/**
 * 별 모양 패턴
 */
export function generateStarPattern() {
  // 별 모양을 그리는 좌표들
  const starCoords = [
    3,           // 꼭대기
    9, 11,       // 위 가지
    16, 17, 18,  // 중간
    22, 24, 26,  // 아래 가지
    31, 33       // 아래 끝
  ];
  
  const selected = [];
  const available = starCoords.filter(n => n <= 45);
  
  // 6개 선택
  while (selected.length < 6 && available.length > 0) {
    const index = Math.floor(Math.random() * available.length);
    selected.push(available.splice(index, 1)[0]);
  }
  
  // 부족하면 채우기
  while (selected.length < 6) {
    const num = Math.floor(Math.random() * 45) + 1;
    if (!selected.includes(num)) selected.push(num);
  }
  
  return {
    numbers: selected.sort((a, b) => a - b),
    patternName: '별을 그리며',
    description: '밤하늘의 별처럼 반짝이는 선택 ⭐',
    funFactor: '⭐⭐⭐⭐',
    visualAppeal: '✨'
  };
}

/**
 * 대각선 패턴
 */
export function generateDiagonalPattern() {
  const diagonals = [
    [1, 9, 17, 25, 33, 41],   // 좌상-우하
    [6, 12, 18, 24, 30, 36],  // 우상-좌하
    [2, 10, 18, 26, 34, 42],  // 변형1
    [5, 11, 17, 23, 29, 35]   // 변형2
  ];
  
  const selected = diagonals[Math.floor(Math.random() * diagonals.length)];
  
  return {
    numbers: selected.sort((a, b) => a - b),
    patternName: '대각선 미학',
    description: '깔끔한 대각선 패턴',
    funFactor: '⭐⭐⭐',
    visualAppeal: '📏'
  };
}

// ============= 개인화 패턴은 제거됨 =============
// 개인 설정의 복잡성으로 인해 개인화 패턴 카테고리는 제거되었습니다.

// ============= 통계적 재미 패턴 =============

/**
 * 핫 넘버 패턴 (자주 나온 번호 - 재미용)
 */
export function generateHotNumbersPattern(historicalData) {
  // 데이터가 없거나 부족한 경우 랜덤 생성
  if (!historicalData || !Array.isArray(historicalData) || historicalData.length < 10) {
    const randomNumbers = [];
    while (randomNumbers.length < 6) {
      const num = Math.floor(Math.random() * 45) + 1;
      if (!randomNumbers.includes(num)) randomNumbers.push(num);
    }
    return {
      numbers: randomNumbers.sort((a, b) => a - b),
      patternName: '핫 넘버 셀렉션',
      description: '인기 번호 스타일 (확률은 동일해요!)',
      funFactor: '⭐⭐⭐',
      temperature: '🔥',
      disclaimer: '과거 빈도는 미래와 무관합니다'
    };
  }

  const frequency = {};
  for (let i = 1; i <= 45; i++) frequency[i] = 0;

  historicalData.forEach(draw => {
    if (draw && Array.isArray(draw.numbers)) {
      draw.numbers.forEach(num => {
        if (num >= 1 && num <= 45) frequency[num]++;
      });
    }
  });

  const sorted = Object.entries(frequency)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 10)
    .map(([num]) => parseInt(num));

  // 상위 10개 중 6개 랜덤 선택
  const selected = [];
  for (let i = 0; i < 6 && sorted.length > 0; i++) {
    const index = Math.floor(Math.random() * sorted.length);
    selected.push(sorted.splice(index, 1)[0]);
  }

  // 부족하면 랜덤으로 채우기
  while (selected.length < 6) {
    const num = Math.floor(Math.random() * 45) + 1;
    if (!selected.includes(num)) selected.push(num);
  }

  return {
    numbers: selected.sort((a, b) => a - b),
    patternName: '핫 넘버 셀렉션',
    description: '최근 인기 번호들 (확률은 동일해요!)',
    funFactor: '⭐⭐⭐',
    temperature: '🔥',
    disclaimer: '과거 빈도는 미래와 무관합니다'
  };
}

/**
 * 콜드 넘버 패턴 (적게 나온 번호 - 재미용)
 */
export function generateColdNumbersPattern(historicalData) {
  // 데이터가 없거나 부족한 경우 랜덤 생성
  if (!historicalData || !Array.isArray(historicalData) || historicalData.length < 10) {
    const randomNumbers = [];
    while (randomNumbers.length < 6) {
      const num = Math.floor(Math.random() * 45) + 1;
      if (!randomNumbers.includes(num)) randomNumbers.push(num);
    }
    return {
      numbers: randomNumbers.sort((a, b) => a - b),
      patternName: '콜드 넘버 셀렉션',
      description: '쉬는 번호 스타일 (확률은 동일해요!)',
      funFactor: '⭐⭐⭐',
      temperature: '❄️',
      disclaimer: '과거 빈도는 미래와 무관합니다'
    };
  }

  const frequency = {};
  for (let i = 1; i <= 45; i++) frequency[i] = 0;

  historicalData.forEach(draw => {
    if (draw && Array.isArray(draw.numbers)) {
      draw.numbers.forEach(num => {
        if (num >= 1 && num <= 45) frequency[num]++;
      });
    }
  });

  const sorted = Object.entries(frequency)
    .sort(([,a], [,b]) => a - b)
    .slice(0, 10)
    .map(([num]) => parseInt(num));

  // 하위 10개 중 6개 랜덤 선택
  const selected = [];
  for (let i = 0; i < 6 && sorted.length > 0; i++) {
    const index = Math.floor(Math.random() * sorted.length);
    selected.push(sorted.splice(index, 1)[0]);
  }

  // 부족하면 랜덤으로 채우기
  while (selected.length < 6) {
    const num = Math.floor(Math.random() * 45) + 1;
    if (!selected.includes(num)) selected.push(num);
  }

  return {
    numbers: selected.sort((a, b) => a - b),
    patternName: '콜드 넘버 셀렉션',
    description: '최근 쉬고 있는 번호들 (확률은 동일해요!)',
    funFactor: '⭐⭐⭐',
    temperature: '❄️',
    disclaimer: '과거 빈도는 미래와 무관합니다'
  };
}

/**
 * 균형잡힌 패턴
 */
export function generateBalancedPattern() {
  const numbers = [];
  
  // 각 구간에서 하나씩
  const ranges = [
    [1, 9], [10, 18], [19, 27], [28, 36], [37, 45]
  ];
  
  ranges.forEach(([min, max]) => {
    const num = Math.floor(Math.random() * (max - min + 1)) + min;
    numbers.push(num);
  });
  
  // 6번째 번호는 랜덤
  numbers.push(Math.floor(Math.random() * 45) + 1);
  
  // 중복 제거
  const unique = [...new Set(numbers)];
  while (unique.length < 6) {
    const num = Math.floor(Math.random() * 45) + 1;
    if (!unique.includes(num)) unique.push(num);
  }
  
  return {
    numbers: unique.slice(0, 6).sort((a, b) => a - b),
    patternName: '완벽한 균형',
    description: '모든 구간을 아우르는 조화',
    funFactor: '⭐⭐⭐⭐',
    balance: '⚖️'
  };
}

/**
 * 남들이 안 쓰는 패턴 (역발상)
 */
export function generateAvoidedPattern() {
  const avoided = [
    [1, 2, 3, 4, 5, 6],        // 연속
    [5, 10, 15, 20, 25, 30],   // 5의 배수
    [7, 14, 21, 28, 35, 42],   // 7의 배수
    [1, 11, 21, 31, 41, 43],   // 10단위
  ];
  
  const selected = avoided[Math.floor(Math.random() * avoided.length)];
  
  return {
    numbers: selected.sort((a, b) => a - b),
    patternName: '역발상 전략',
    description: '남들이 기피하는 패턴 (당첨금이 클지도?)',
    funFactor: '⭐⭐⭐⭐⭐',
    strategy: '🎭',
    insight: '당첨 시 독식 가능성 UP'
  };
}

// ============= 랜덤 재미 패턴 =============

/**
 * 카오스 패턴
 */
export function generateChaosPattern() {
  const numbers = new Set();
  
  // 완전 무작위 + 카오스 이론
  while (numbers.size < 6) {
    const seed = Date.now() * Math.random();
    const chaos = Math.sin(seed) * 10000;
    const num = Math.floor(Math.abs(chaos) % 45) + 1;
    numbers.add(num);
  }
  
  return {
    numbers: Array.from(numbers).sort((a, b) => a - b),
    patternName: '카오스 이론',
    description: '나비효과가 만든 번호',
    funFactor: '⭐⭐⭐⭐',
    chaosLevel: '🌀'
  };
}

/**
 * 양자 패턴 (시간 기반 의사 랜덤)
 */
export function generateQuantumPattern() {
  const numbers = [];
  const now = Date.now();
  
  for (let i = 0; i < 6; i++) {
    // 시간과 위치를 결합한 의사 양자 상태
    const quantum = (now * (i + 1) * Math.PI) % 45 + 1;
    const collapsed = Math.floor(quantum);
    numbers.push(collapsed);
  }
  
  // 중복 제거 및 보정
  const unique = [...new Set(numbers)];
  while (unique.length < 6) {
    unique.push(Math.floor(Math.random() * 45) + 1);
  }
  
  return {
    numbers: unique.slice(0, 6).sort((a, b) => a - b),
    patternName: '양자 붕괴',
    description: '관측 순간 결정되는 번호',
    funFactor: '⭐⭐⭐⭐⭐',
    quantum: '⚛️',
    schrodinger: '📦'
  };
}

/**
 * 우주 패턴 (천문학적 수)
 */
export function generateCosmicPattern() {
  const cosmicNumbers = [
    3,   // 삼체 문제
    7,   // 행운의 수
    12,  // 황도 12궁
    13,  // 달의 주기
    27,  // 달의 공전
    42   // 우주의 답
  ];
  
  // 우주적 변형
  const numbers = cosmicNumbers.map(n => {
    const variation = Math.floor(Math.random() * 3) - 1;
    return Math.max(1, Math.min(45, n + variation));
  });
  
  return {
    numbers: numbers.sort((a, b) => a - b),
    patternName: '우주의 속삭임',
    description: '별들이 알려준 번호',
    funFactor: '⭐⭐⭐⭐⭐',
    cosmic: '🌌',
    astrology: '🔮'
  };
}

/**
 * 자연 패턴 (자연수 기반)
 */
export function generateNaturePattern() {
  const goldenRatio = 1.618;
  const numbers = [];
  
  // 황금비 기반 생성
  let current = 1;
  for (let i = 0; i < 8; i++) {
    current = Math.floor(current * goldenRatio);
    if (current <= 45 && current > 0) {
      numbers.push(current);
    }
  }
  
  // 자연수 추가
  while (numbers.length < 6) {
    const natural = Math.floor(Math.random() * 45) + 1;
    if (!numbers.includes(natural)) {
      numbers.push(natural);
    }
  }
  
  return {
    numbers: numbers.slice(0, 6).sort((a, b) => a - b),
    patternName: '자연의 선물',
    description: '황금비가 만든 조화',
    funFactor: '⭐⭐⭐⭐',
    nature: '🌿',
    golden: '✨'
  };
}

// ============= 메인 패턴 생성 함수 =============

/**
 * 패턴 타입에 따라 적절한 생성기 호출
 */
export function generatePattern(type, options = {}) {
  const generators = {
    // 수학적 패턴
    fibonacci: generateFibonacciPattern,
    prime: generatePrimePattern,
    squares: generateSquaresPattern,
    triangular: generateTriangularPattern,
    
    // 시각적 패턴
    heart: generateHeartPattern,
    star: generateStarPattern,
    diagonal: generateDiagonalPattern,
    
    // 통계적 재미
    hot: () => generateHotNumbersPattern(options.historicalData || []),
    cold: () => generateColdNumbersPattern(options.historicalData || []),
    balanced: generateBalancedPattern,
    avoided: generateAvoidedPattern,
    
    // 랜덤 재미
    chaos: generateChaosPattern,
    quantum: generateQuantumPattern,
    cosmic: generateCosmicPattern,
    nature: generateNaturePattern
  };
  
  const generator = generators[type];
  if (!generator) {
    throw new Error(`Unknown pattern type: ${type}`);
  }
  
  // 5게임 생성
  const games = [];
  for (let i = 0; i < 5; i++) {
    const result = generator();
    
    // 게임별 고유성 추가 (시드 다양화)
    const timeVariation = Date.now() + i * 1000;
    const gameResult = {
      ...result,
      gameNumber: i + 1,
      numbers: addVariation(result.numbers, i), // 게임별 변화 추가
      type,
      timestamp: timeVariation,
      probability: '1 / 8,145,060',
      actualChance: '0.0000123%',
      disclaimer: '모든 번호 조합의 당첨 확률은 동일합니다. 이것은 재미를 위한 선택입니다.',
      enjoymentScore: result.funFactor || '⭐⭐⭐'
    };
    
    games.push(gameResult);
  }
  
  return games;
}

/**
 * 게임별 변화를 추가하여 6게임이 모두 다르도록 함
 */
function addVariation(originalNumbers, gameIndex) {
  const numbers = [...originalNumbers];
  
  // 게임 인덱스를 기반으로 변화 추가
  for (let i = 0; i < gameIndex + 1; i++) {
    // 랜덤하게 1-2개 번호를 다른 번호로 교체
    const replaceCount = Math.min(Math.floor(Math.random() * 2) + 1, 2);
    
    for (let j = 0; j < replaceCount; j++) {
      const replaceIndex = Math.floor(Math.random() * numbers.length);
      let newNumber;
      
      do {
        newNumber = Math.floor(Math.random() * 45) + 1;
      } while (numbers.includes(newNumber));
      
      numbers[replaceIndex] = newNumber;
    }
  }
  
  return numbers.sort((a, b) => a - b);
}

/**
 * 진정한 메가 콤보 패턴 (5게임, 각각 다른 패턴 조합)
 */
export function generateMegaPattern(patterns = []) {
  const games = [];
  
  // 모든 가능한 패턴 타입들 (개인화 패턴 제외)
  const allPatternTypes = [
    'fibonacci', 'prime', 'squares', 'triangular',
    'heart', 'star', 'diagonal', 
    'hot', 'cold', 'balanced', 'avoided',
    'chaos', 'quantum', 'cosmic', 'nature'
  ];
  
  // 각 게임마다 다른 패턴 조합 생성
  for (let gameNum = 0; gameNum < 5; gameNum++) {
    // 각 게임마다 2-3개의 랜덤 패턴 선택 (중복 없이)
    const gamePatterns = [];
    const availablePatterns = [...allPatternTypes];
    const patternCount = Math.floor(Math.random() * 2) + 2; // 2-3개
    
    for (let i = 0; i < patternCount && availablePatterns.length > 0; i++) {
      const randomIndex = Math.floor(Math.random() * availablePatterns.length);
      gamePatterns.push(availablePatterns.splice(randomIndex, 1)[0]);
    }
    
    // 선택된 패턴들로 번호 조합 생성
    const allNumbers = new Set();
    gamePatterns.forEach(patternType => {
      const singlePattern = generateSinglePatternNumbers(patternType);
      // 각 패턴에서 2-3개씩 가져오기
      const sampleSize = Math.floor(6 / gamePatterns.length) + (Math.random() > 0.5 ? 1 : 0);
      const shuffled = [...singlePattern].sort(() => Math.random() - 0.5);
      shuffled.slice(0, Math.min(sampleSize, singlePattern.length)).forEach(n => allNumbers.add(n));
    });
    
    // 6개로 조정
    const numbers = Array.from(allNumbers);
    while (numbers.length > 6) {
      numbers.splice(Math.floor(Math.random() * numbers.length), 1);
    }
    while (numbers.length < 6) {
      const num = Math.floor(Math.random() * 45) + 1;
      if (!numbers.includes(num)) numbers.push(num);
    }
    
    // 패턴 이름들을 사용자 친화적으로 변환
    const patternNames = gamePatterns.map(getPatternDisplayName);
    
    games.push({
      gameNumber: gameNum + 1,
      numbers: numbers.sort((a, b) => a - b),
      patternName: `메가 콤보 ${String.fromCharCode(65 + gameNum)}`,
      description: `${patternNames.join(' + ')} 조합`,
      selectedPatterns: gamePatterns,
      funFactor: '⭐⭐⭐⭐⭐',
      combo: '🎰',
      probability: '1 / 8,145,060',
      actualChance: '0.0000123%',
      disclaimer: '여러 패턴을 섞어도 확률은 같아요!'
    });
  }
  
  return games;
}

/**
 * 단일 패턴에서 번호만 추출하는 헬퍼 함수
 */
function generateSinglePatternNumbers(type) {
  const generators = {
    fibonacci: generateFibonacciPattern,
    prime: generatePrimePattern,
    squares: generateSquaresPattern,
    triangular: generateTriangularPattern,
    heart: generateHeartPattern,
    star: generateStarPattern,
    diagonal: generateDiagonalPattern,
    chaos: generateChaosPattern,
    quantum: generateQuantumPattern,
    cosmic: generateCosmicPattern,
    nature: generateNaturePattern
  };
  
  const generator = generators[type];
  if (!generator) {
    // 기본 랜덤 생성
    const numbers = [];
    while (numbers.length < 6) {
      const num = Math.floor(Math.random() * 45) + 1;
      if (!numbers.includes(num)) numbers.push(num);
    }
    return numbers;
  }
  
  return generator().numbers;
}

/**
 * 패턴 타입을 사용자 친화적 이름으로 변환
 */
function getPatternDisplayName(patternType) {
  const displayNames = {
    fibonacci: '피보나치',
    prime: '소수',
    squares: '제곱수',
    triangular: '삼각수',
    heart: '하트',
    star: '별',
    diagonal: '대각선',
    hot: '핫넘버',
    cold: '콜드넘버',
    balanced: '균형',
    avoided: '역발상',
    chaos: '카오스',
    quantum: '양자',
    cosmic: '우주',
    nature: '자연'
  };
  
  return displayNames[patternType] || patternType;
}

/**
 * 패턴 추천 시스템
 */
export function recommendPattern(mood = 'playful') {
  const recommendations = {
    playful: ['heart', 'star', 'cosmic', 'quantum'],
    serious: ['fibonacci', 'prime', 'balanced'],
    lucky: ['lucky', 'avoided', 'hot'],
    creative: ['chaos', 'nature', 'triangular'],
    personal: ['birthday', 'anniversary', 'favorite']
  };
  
  const options = recommendations[mood] || recommendations.playful;
  const selected = options[Math.floor(Math.random() * options.length)];
  
  return {
    recommended: selected,
    reason: `현재 기분(${mood})에 어울리는 패턴입니다`,
    alternates: options.filter(p => p !== selected)
  };
}